import { DaysOfWeek, INVALID_FEATURE } from 'enums';
import { ALL_DAYS_OF_WEEK } from 'constants/daysOfWeek';
import { MAXIMUM_ALLOWED_MINUTES } from 'constants/general';
import { ActivityType } from 'interfaces/common.interface';
import {
  ActivityHabitValidationContext,
  HabitEditFormFieldErrors,
  HabitEditFormValues
} from 'interfaces/habit-edit-form.interface';
import { ValidationError } from 'interfaces/setting.interface';
import { t } from 'services/i18n.service';
import { convertToSeconds } from 'utils/helpers';
import { normalizeDaysOfWeekValue } from 'utils/daysOfWeek.util';
import { hasCompletionRequirement, isValidHabitName } from 'utils/validators';

export const getInitialHabitEditDaysOfWeek = (
  days?: (DaysOfWeek | string)[]
): DaysOfWeek[] => {
  if (!days || days.length === 0) {
    return [...ALL_DAYS_OF_WEEK];
  }
  return normalizeDaysOfWeekValue(days);
};

export const isHabitEditDurationValid = (durationSeconds: number): boolean => {
  const duration = Number(durationSeconds ?? 0);
  return duration > 0 && duration <= convertToSeconds(MAXIMUM_ALLOWED_MINUTES);
};

export const isHabitEditFormValid = (values: HabitEditFormValues): boolean => {
  const trimmedName = values.name.trim();
  if (!trimmedName || !isValidHabitName(trimmedName)) {
    return false;
  }

  if (values.daysOfWeek !== undefined && values.daysOfWeek.length === 0) {
    return false;
  }

  if (values.useTimer) {
    return isHabitEditDurationValid(values.durationSeconds);
  }

  return Boolean(String(values.completionValue ?? '').trim());
};

/** i18n keys for field errors; resolve with `t()` in components. */
export const getHabitEditFormFieldErrorKeys = (
  values: HabitEditFormValues
): HabitEditFormFieldErrors => {
  const errors: HabitEditFormFieldErrors = {};
  const trimmedName = values.name.trim();

  if (!trimmedName) {
    errors.name = 'validation.empty_content';
  } else if (!isValidHabitName(trimmedName)) {
    errors.name = 'validation.invalid_characters';
  }

  if (values.daysOfWeek !== undefined && values.daysOfWeek.length === 0) {
    errors.daysOfWeek = 'validation.select_at_least_one_day_for_habit';
  }

  if (values.useTimer) {
    if (!isHabitEditDurationValid(values.durationSeconds)) {
      errors.duration = 'validate_duration_min_max';
    }
  } else if (!String(values.completionValue ?? '').trim()) {
    errors.completion = 'validation.empty_content';
  }

  return errors;
};

export const activityToHabitEditFormValues = (
  activity: ActivityType
): HabitEditFormValues => ({
  name: activity.name ?? '',
  useTimer: !hasCompletionRequirement(activity),
  durationSeconds: activity.duration_seconds ?? 0,
  completionValue: activity.completion_requirements
});

export const isActivityHabitFieldsValid = (activity: ActivityType): boolean =>
  isHabitEditFormValid(activityToHabitEditFormValues(activity));

export const getActivityHabitFieldErrorKeys = (
  activity: ActivityType
): HabitEditFormFieldErrors =>
  getHabitEditFormFieldErrorKeys(activityToHabitEditFormValues(activity));

/** Pushes timer/completion field errors for settings validation lists. */
export const appendActivityHabitFieldValidationErrors = (
  errors: ValidationError[],
  activity: ActivityType,
  context: ActivityHabitValidationContext
): void => {
  const fieldErrors = getActivityHabitFieldErrorKeys(activity);
  const durationFeature = context.durationFeature ?? INVALID_FEATURE.DURATION;
  const completionFeature =
    context.completionFeature ?? INVALID_FEATURE.HABIT_NAME;

  if (fieldErrors.duration) {
    errors.push({
      tab: { title: context.tabTitle, habit: context.habitLabel },
      feature: durationFeature,
      message: t(fieldErrors.duration),
      invalid_value: String(activity.duration_seconds ?? 0),
      position: context.position,
      activityId: context.activityId,
      activityType: context.activityType,
      habitPosition: context.habitPosition,
      customRoutinePosition: context.customRoutinePosition,
      habitName: context.habitName
    });
  }

  if (fieldErrors.completion) {
    errors.push({
      tab: { title: context.tabTitle, habit: context.habitLabel },
      feature: completionFeature,
      message: t(fieldErrors.completion),
      invalid_value: activity.completion_requirements ?? '',
      position: context.position,
      activityId: context.activityId,
      activityType: context.activityType,
      habitPosition: context.habitPosition,
      customRoutinePosition: context.customRoutinePosition,
      habitName: context.habitName
    });
  }
};
